// Mercapoli - Capacitor Native Service
// Handles native functionality for iOS/Android WebView apps
// Compatible with Capacitor 6.x

import { Capacitor } from '@capacitor/core';
import { SplashScreen } from '@capacitor/splash-screen';
import { StatusBar, Style } from '@capacitor/status-bar';
import { Network } from '@capacitor/network';
import { PushNotifications } from '@capacitor/push-notifications';
import { App } from '@capacitor/app';

// Check if running in native app
export const isNative = Capacitor.isNativePlatform();
export const platform = Capacitor.getPlatform(); // 'ios', 'android', or 'web'

/**
 * Initialize native app features
 * Call this once when app starts
 */
export const initNativeApp = async () => {
  if (!isNative) return;

  try {
    // Configure status bar
    await StatusBar.setStyle({ style: Style.Dark });
    if (platform === 'android') {
      await StatusBar.setBackgroundColor({ color: '#0A0F2C' });
    }

    // Setup network listener
    setupNetworkListener();

    // Setup app state listener (foreground/background)
    setupAppStateListener();

    // Hide splash screen after initialization
    await hideSplash();

    console.log('✅ Native app initialized on', platform);
  } catch (error) {
    console.error('Error initializing native app:', error);
  }
};

/**
 * Hide splash screen with fade
 */
export const hideSplash = async () => {
  if (!isNative) return;
  
  try {
    await SplashScreen.hide({
      fadeOutDuration: 300
    });
  } catch (error) {
    console.error('Error hiding splash:', error);
  }
};

/**
 * Show splash screen (useful for reloads)
 */
export const showSplash = async () => {
  if (!isNative) return;
  
  try {
    await SplashScreen.show({
      autoHide: false,
      showDuration: 2000
    });
  } catch (error) {
    console.error('Error showing splash:', error);
  }
};

// Network status
let networkCallback = null;

/**
 * Setup network status listener
 */
const setupNetworkListener = () => {
  Network.addListener('networkStatusChange', (status) => {
    console.log('Network status changed:', status);
    if (networkCallback) {
      networkCallback(status);
    }
  });
};

/**
 * Register callback for network changes
 */
export const onNetworkChange = (callback) => {
  networkCallback = callback;
};

/**
 * Get current network status
 */
export const getNetworkStatus = async () => {
  try {
    return await Network.getStatus();
  } catch (error) {
    return { connected: true, connectionType: 'unknown' };
  }
};

// App state (foreground/background)
let appStateCallback = null;

/**
 * Setup app state listener
 */
const setupAppStateListener = () => {
  App.addListener('appStateChange', ({ isActive }) => {
    console.log('App state changed, active:', isActive);
    if (appStateCallback) {
      appStateCallback(isActive);
    }
  });
};

/**
 * Register callback for app state changes
 */
export const onAppStateChange = (callback) => {
  appStateCallback = callback;
};

// ==================== PUSH NOTIFICATIONS ====================

/**
 * Request push notification permissions
 * Returns the FCM/APNS token if granted
 */
export const requestPushPermission = async () => {
  if (!isNative) {
    console.log('Push notifications not available on web');
    return null;
  }

  try {
    // Check current permission status
    const permStatus = await PushNotifications.checkPermissions();
    
    if (permStatus.receive === 'prompt') {
      // Request permission
      const result = await PushNotifications.requestPermissions();
      if (result.receive !== 'granted') {
        console.log('Push permission denied');
        return null;
      }
    } else if (permStatus.receive !== 'granted') {
      console.log('Push permission not granted');
      return null;
    }

    // Register for push
    await PushNotifications.register();

    // Wait for registration token
    return new Promise((resolve) => {
      PushNotifications.addListener('registration', (token) => {
        console.log('Push registration success, token:', token.value);
        resolve(token.value);
      });

      PushNotifications.addListener('registrationError', (error) => {
        console.error('Push registration error:', error);
        resolve(null);
      });

      // Timeout after 10 seconds
      setTimeout(() => resolve(null), 10000);
    });
  } catch (error) {
    console.error('Error requesting push permission:', error);
    return null;
  }
};

/**
 * Setup push notification listeners
 */
export const setupPushListeners = (options = {}) => {
  if (!isNative) return;

  const { onReceive, onAction, onError } = options;

  // Notification received while app is in foreground
  PushNotifications.addListener('pushNotificationReceived', (notification) => {
    console.log('Push notification received:', notification);
    if (onReceive) onReceive(notification);
  });

  // User tapped on notification
  PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
    console.log('Push notification action:', action);
    if (onAction) onAction(action);
  });

  // Registration error
  PushNotifications.addListener('registrationError', (error) => {
    console.error('Push registration error:', error);
    if (onError) onError(error);
  });
};

// ==================== DEEP LINKING ====================

/**
 * Setup deep link handler
 * Handles Stripe return URLs and other deep links
 */
export const setupDeepLinks = (callback) => {
  if (!isNative) return;

  // Handle app opened via URL
  App.addListener('appUrlOpen', (event) => {
    console.log('App opened with URL:', event.url);
    
    // Parse URL to handle Stripe returns
    const url = new URL(event.url);
    
    // Handle Stripe payment success/cancel
    if (url.pathname.includes('pago-exitoso') || url.host === 'pago-exitoso') {
      const sessionId = url.searchParams.get('session_id');
      const pedidoId = url.searchParams.get('pedido_id');
      
      // Navigate to payment success page
      if (callback) {
        callback({
          type: 'stripe_payment_success',
          url: event.url,
          sessionId,
          pedidoId
        });
      }
      return;
    }
    
    if (callback) callback({ type: 'url', url: event.url });
  });
};

/**
 * Handle WebView navigation for Stripe checkout
 * Returns URL to use for Stripe success/cancel
 */
export const getStripeReturnUrl = (pedidoId, type = 'success') => {
  if (!isNative) {
    // Web: use regular URL
    const baseUrl = window.location.origin;
    if (type === 'success') {
      return `${baseUrl}/app/pago-exitoso?pedido_id=${pedidoId}`;
    }
    return `${baseUrl}/app/checkout?cancelled=true&pedido_id=${pedidoId}`;
  }
  
  // Native: use custom URL scheme
  const scheme = 'mercapoli';
  if (type === 'success') {
    return `${scheme}://pago-exitoso?pedido_id=${pedidoId}`;
  }
  return `${scheme}://checkout?cancelled=true&pedido_id=${pedidoId}`;
};

// ==================== BACK BUTTON (Android) ====================

/**
 * Handle Android back button
 */
export const setupBackButton = (callback) => {
  if (!isNative || platform !== 'android') return;

  App.addListener('backButton', (event) => {
    console.log('Back button pressed, canGoBack:', event.canGoBack);
    if (callback) {
      callback(event);
    } else if (!event.canGoBack) {
      // Exit app if can't go back
      App.exitApp();
    }
  });
};

// Export default object
export default {
  isNative,
  platform,
  initNativeApp,
  hideSplash,
  showSplash,
  onNetworkChange,
  getNetworkStatus,
  onAppStateChange,
  requestPushPermission,
  setupPushListeners,
  setupDeepLinks,
  setupBackButton,
  getStripeReturnUrl
};
